import {Button, ButtonGroup, closeDialog, showNotification} from 'platform/components';
import {Box, Space} from 'platform/foundation';

import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {DmsDataGrid} from '@dms/shared';

import {useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {isStaleRevisionError} from '../utils/handleOfferAlternativeError';

const PREDEFINED_COLUMNS = ['name'] as const;

interface AlternativeUpsellCatalogDialogProps {
  dialogId: string;
  /** Adds the picked product as a new upsell line; rejects when the save failed. */
  onSelect: (productId: string) => Promise<unknown>;
}

/**
 * Same catalogue grid as the deal vehicle's "Add item from catalog". Only the product id is sent:
 * the backend takes the product's name, prices and VAT and rejects a product in another currency.
 */
export function AlternativeUpsellCatalogDialog(props: AlternativeUpsellCatalogDialogProps) {
  const navigate = useNavigate();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (actionKey !== 'select') {
      return;
    }
    const productId = isArray(rowId) ? rowId[0] : rowId;
    const name = getColumnData<string>('name')?.value;

    props
      .onSelect(productId)
      .then(() => {
        closeDialog(props.dialogId);
        showNotification.success(i18n.t('entity.productCatalog.notifications.itemAdded', {name}));
      })
      // onSelect reports its own failure. A stale revision closes, so reopening the dialog
      // saves against the refreshed alternative, like the other alternative dialogs.
      .catch((error: unknown) => {
        if (isStaleRevisionError(error)) {
          closeDialog(props.dialogId);
        }
      });
  };

  return (
    <>
      <Box height={70}>
        <DmsDataGrid
          gridCode="select_from_product_catalog"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          emptyState={{
            action: {
              onClick: () => navigate(settingsRoutes.productCatalog),
              title: i18n.t('entity.productCatalog.actions.addProduct'),
            },
          }}
          data-testid="offerAlternativeUpsellCatalog-grid"
        />
      </Box>

      <Space vertical={4} />

      <ButtonGroup align="right">
        <Button
          variant="secondary"
          onClick={() => closeDialog(props.dialogId)}
          title={i18n.t('general.actions.cancel')}
        />
      </ButtonGroup>
    </>
  );
}
